import { useEffect, useState } from 'react';
import type { PaigeDestinationsBridge, PaigeFilingKind, PaigeFilingPreview } from './paige.global';

/** Where Paige would file the job's latest document here, or Paige's reason it cannot say. */
export interface FilingPreviewState {
  preview: PaigeFilingPreview | null;
  error: string | null;
}

/** How long a change of place settles before the preview is asked for again. */
export const previewDelayMs = 350;

/**
 * The preview of where a storage or Nucleus One destination files (ADR-0016, ADR-0017): rendered by Paige, as a delivery
 * renders it, under the job's file naming for the job's latest exported document, whenever the
 * chosen place stops changing for a moment. A later answer always replaces an earlier one; an
 * answer for a place no longer on screen is dropped.
 */
export function useFilingPreview(
  bridge: PaigeDestinationsBridge,
  kind: PaigeFilingKind,
  locationPath: string,
): FilingPreviewState {
  const [state, setState] = useState<FilingPreviewState>({ preview: null, error: null });
  useEffect(() => {
    let live = true;
    const timer = window.setTimeout(() => {
      bridge
        .preview(kind, locationPath || undefined)
        .then((preview) => live && setState({ preview, error: null }))
        .catch(
          (failure: unknown) =>
            live &&
            setState({
              preview: null,
              error: failure instanceof Error ? failure.message : 'Paige could not render the name',
            }),
        );
    }, previewDelayMs);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [bridge, kind, locationPath]);
  return state;
}
